import { usePermissions } from 'contexts';
import React, { useEffect } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useNavigate } from 'react-router';
import { setCurrentYearIdAction } from 'store/years/years.slice';
import {
  createYearThunk,
  getYearsThunk,
  removeYearThunk,
  updateYearThunk,
} from 'store/years/years.thunks';

import { useAppDispatch, useAppSelector } from '../../../store';
import { vocabulary } from '../../../vocabulary/german';
import { CreateButton } from '../../buttons/create-button/create-button';
import { EntityPanel } from '../../panels/entity-panel/entity-panel';
import { Container } from '../../template/container/container';
import { ConfirmationModal } from '../../template/modals/confirmation-modal';
import { NotFoundMessage } from '../../template/not-found-message/not-found-message';
import { PageHeader } from '../../template/page-header/page-header';
import { Spinner } from '../../template/spinner/spinner';

const YearSelectionPage = () => {
  const { permissions, role } = usePermissions();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    list: yearsList,
    isGetYearsThunkPending,
    isCreateYearThunkPending,
  } = useAppSelector((state) => state.years);
  const hasYears = yearsList.length > 0;

  useEffect(() => {
    dispatch(getYearsThunk());
  }, [dispatch]);

  const showRemoveConfirmationModal = (
    id: number,
    event: React.MouseEvent<HTMLElement>,
  ): void => {
    event.stopPropagation();

    confirmAlert({
      customUI: ({ onClose }): JSX.Element => {
        return (
          <ConfirmationModal
            onClose={onClose}
            onOk={() => dispatch(removeYearThunk(id))}
          />
        );
      },
    });
  };

  const checkFreezeUnfreeze = (id: number, enabled: boolean) => {
    dispatch(updateYearThunk({ id, enabled: !enabled }));
  };

  const checkFrozenYearGoals = (id: number, frozenYearGoals: boolean) => {
    dispatch(
      updateYearThunk({
        id,
        frozenYearGoals: !frozenYearGoals,
      }),
    );
  };

  const createYear = (): void => {
    dispatch(createYearThunk());
  };

  const selectYear = (
    id: number,
    year: string,
    isFrozen: boolean,
    isYearGoalFrozen: boolean,
  ) => {
    dispatch(
      setCurrentYearIdAction({
        id,
        name: Number(year),
        isFrozen,
        isYearGoalFrozen,
      }),
    );
    navigate('/');
  };

  return (
    <div className={'page-content'}>
      <PageHeader
        title={vocabulary.labels.yearSelections}
        btnText=""
        downloadReport={null}
      />
      <Container>
        {isGetYearsThunkPending ? (
          <Spinner />
        ) : (
          <>
            {hasYears && (
              <div className={'entities-list'}>
                {yearsList.map((year) => {
                  const itemClass = year.enabled ? '' : 'year-disabled';
                  return (
                    <div className="list-item" key={year.id}>
                      <EntityPanel
                        key={year.id}
                        title={year.title}
                        id={year.id}
                        onClick={() =>
                          selectYear(
                            year.id,
                            year.title,
                            !year.enabled,
                            year.frozenYearGoals ?? false,
                          )
                        }
                        className={itemClass}
                        isDeletable={role === 'admin'}
                        onDelete={(event) =>
                          showRemoveConfirmationModal(year.id, event)
                        }
                      />
                      {permissions.freezeYears === true && (
                        <div
                          className={`year-${year.enabled ? 'freeze' : 'unfreeze'}`}
                          onClick={() => {
                            checkFreezeUnfreeze(year.id, year.enabled);
                          }}
                        />
                      )}
                      {year.enabled && permissions.freezeYearsGoals && (
                        <div
                          className={`years-goals-${!year.frozenYearGoals ? 'freeze' : 'unfreeze'}`}
                          onClick={() =>
                            checkFrozenYearGoals(
                              year.id,
                              year.frozenYearGoals ?? false,
                            )
                          }
                        />
                      )}
                      {role !== 'viewer' &&
                        year.enabled &&
                        year.frozenYearGoals && (
                          <div>
                            &nbsp;&nbsp;{vocabulary.labels.freezeGoalYear}
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            )}
            {!hasYears && <NotFoundMessage message="Jahre nicht gefunden" />}
            {role === 'admin' && (
              <CreateButton
                text={vocabulary.buttons.createNewYear}
                onClick={createYear}
                disabled={isCreateYearThunkPending}
              />
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default YearSelectionPage;
